import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useDeleteManual, useManual } from '../api/queries'
import { useAuth } from '../auth/AuthProvider'
import { ManualText } from '../components/ManualText'
import { Card, ErrorBox, Loading } from '../components/ui'
import { useAuthedBlob } from '../lib/photos'
import { formatDateTime } from '../lib/format'
import { describeSize } from './ManualsPage'

/**
 * Læsevisningen af en manual (docs/DECISIONS.md beslutning 063).
 *
 * <b>Hvem der har lavet den, står øverst</b> og ikke som en fodnote. En manual
 * er en forskrift: følger nogen den, og står der noget forkert, skal man kunne
 * finde den, der ved, hvorfor der står, som der gør - uden at grave i
 * aktivitetsloggen.
 *
 * Siden kan printes (og dermed gemmes som PDF) med browserens egen udskrift:
 * knapper og menuer forsvinder, teksten bliver stående.
 */
export function ManualDetailPage() {
  const { id } = useParams()
  const manualId = Number(id)
  const navigate = useNavigate()

  const { isProjectManager, isAdmin } = useAuth()
  const { data, isLoading, error } = useManual(manualId)
  const remove = useDeleteManual()

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!data) return <ErrorBox error={new Error('Manualen findes ikke.')} />

  const manual = data.manual

  async function deleteManual() {
    if (!window.confirm(`Slet manualen "${manual.title}"? Filerne ryger i SharePoints papirkurv.`)) {
      return
    }

    await remove.mutateAsync(manual.id)
    navigate('/manuals')
  }

  return (
    <div className="stack manual-page" style={{ gap: 16 }}>
      <Card
        actions={
          <div className="btn-group no-print">
            <button className="btn btn--sm" onClick={() => window.print()}>
              Print
            </button>
            {isProjectManager && (
              <Link className="btn btn--sm" to={`/manuals/${manual.id}/edit`}>
                Rediger
              </Link>
            )}
            {isAdmin && (
              <button className="btn btn--sm" onClick={deleteManual} disabled={remove.isPending}>
                Slet
              </button>
            )}
          </div>
        }
      >
        <div className="tiny muted no-print">
          <Link to="/manuals">← Manualer</Link>
        </div>

        <h1 className="page-title" style={{ marginBottom: 4, marginTop: 8 }}>
          {manual.title}
        </h1>

        {manual.summary && <div className="small">{manual.summary}</div>}

        {!manual.isActive && (
          <div className="pill" style={{ background: '#FEF3C7', color: '#92400E', marginTop: 8 }}>
            Udgået - den står her, fordi nogen kan have arbejdet efter den
          </div>
        )}

        {/* Hvem der har lavet den. Det er hele grunden til, at felterne findes
            (beslutning 063), saa de staar oeverst og ikke nederst. */}
        <div className="small muted" style={{ marginTop: 12 }}>
          {manual.kind === 'file' ? 'Lagt op af' : 'Skrevet af'}{' '}
          <strong>{manual.createdBy?.displayName ?? 'ukendt'}</strong> den{' '}
          {formatDateTime(manual.createdAt)}
          {manual.updatedBy && manual.updatedAt !== manual.createdAt && (
            <>
              <br />
              Sidst rettet af <strong>{manual.updatedBy.displayName}</strong> den{' '}
              {formatDateTime(manual.updatedAt)}
            </>
          )}
        </div>

        {(manual.categoryName || manual.deviceTypes.length > 0 || manual.templates.length > 0) && (
          <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
            {manual.categoryName && (
              <span className="pill" style={{ background: '#F1F5F9', color: '#475569' }}>
                {manual.categoryName}
              </span>
            )}
            {manual.deviceTypes.map((type) => (
              <span key={type} className="pill" style={{ background: '#E0F2FE', color: '#075985' }}>
                {type}
              </span>
            ))}
            {manual.templates.map((template) => (
              <span key={template.id} className="pill" style={{ background: '#EDE9FE', color: '#5B21B6' }}>
                {template.name}
              </span>
            ))}
          </div>
        )}
      </Card>

      {manual.kind === 'file' ? (
        <ManualFileCard manual={manual} />
      ) : (
        <Card>
          {data.content?.trim() ? (
            <ManualText content={data.content} manualId={manual.id} images={data.images} />
          ) : (
            <div className="muted small">
              Manualen er oprettet, men der er ikke skrevet noget i den endnu.
            </div>
          )}
        </Card>
      )}
    </div>
  )
}

/**
 * Den uploadede fil.
 *
 * <b>Den hentes først, når man trykker.</b> Filen ligger i SharePoint og skal
 * streames gennem API-et, og en leverandørmanual på 40 MB, ingen har bedt om,
 * gør siden ubrugelig på en telefon i et teknikrum.
 *
 * Den kan ikke hentes med et almindeligt link: API-et kræver et token
 * (beslutning 023 - adgangen styres af systemet, ikke af et delingslink), og et
 * `iframe src` sender ingen headers. Derfor hentes den som blob og vises fra en
 * object-URL.
 */
function ManualFileCard({
  manual
}: {
  manual: {
    id: number
    fileName?: string | null
    contentType?: string | null
    sizeBytes?: number | null
  }
}) {
  const [show, setShow] = useState(false)

  if (!manual.fileName) {
    return (
      <Card title="Dokument">
        <div className="muted small">
          Der er ikke lagt en fil op endnu. Åbn "Rediger" og vælg dokumentet.
        </div>
      </Card>
    )
  }

  return (
    <Card title="Dokument">
      <div className="small muted" style={{ marginBottom: 10 }}>
        {manual.fileName} · {describeSize(manual.sizeBytes)}
      </div>

      {show ? (
        <ManualFileViewer manual={manual} />
      ) : (
        <button className="btn btn--primary no-print" onClick={() => setShow(true)}>
          Hent dokumentet
        </button>
      )}
    </Card>
  )
}

function ManualFileViewer({
  manual
}: {
  manual: { id: number; fileName?: string | null; contentType?: string | null }
}) {
  const url = useAuthedBlob(`/api/manuals/${manual.id}/file`)

  if (!url) {
    return <div className="small muted">Henter dokumentet ...</div>
  }

  const isPdf = manual.contentType === 'application/pdf'

  return (
    <div className="stack" style={{ gap: 10 }}>
      {isPdf ? (
        <iframe className="manual-pdf no-print" src={url} title={manual.fileName ?? 'Dokument'} />
      ) : (
        <div className="small">Filtypen kan ikke vises her - åbn den i stedet.</div>
      )}

      <div className="no-print">
        <a className="btn btn--sm" href={url} target="_blank" rel="noreferrer">
          Åbn i ny fane
        </a>
      </div>
    </div>
  )
}
